
import { useState } from "react";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

interface FeedbackQuestionProps {
  itemId: string;
  itemType: 'blog' | 'lesson';
}

const FeedbackQuestion = ({ itemId, itemType }: FeedbackQuestionProps) => {
  const [feedback, setFeedback] = useState<'like' | 'dislike' | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const handleFeedback = async (type: 'like' | 'dislike') => {
    if (!user) {
      toast({
        title: "Login Required",
        description: "Please login to provide feedback",
        action: (
          <Button onClick={() => navigate('/login')} variant="outline" size="sm">
            Login
          </Button>
        ),
      });
      return;
    }
    
    try {
      setIsSubmitting(true);
      setFeedback(type);
      
      const feedbackData = itemType === 'blog' 
        ? { blog_post_id: itemId, type, user_id: user.id }
        : { lesson_id: itemId, type, user_id: user.id };
        
      const tableName = itemType === 'blog' ? 'blog_feedback' : 'lesson_feedback';
      
      const { error } = await supabase
        .from(tableName)
        .insert(feedbackData);
      
      if (error) {
        if (error.code === '23505') {
          toast({
            title: "Feedback Already Submitted",
            description: "Thank you for your previous feedback"
          });
          return;
        }
        throw error;
      }
      
      toast({
        title: "Thank you for your feedback!",
        description: "Your input helps improve our content."
      });
    } catch (error: any) {
      console.error('Error submitting feedback:', error);
      toast({
        title: "Unable to submit feedback",
        description: error.message,
        variant: "destructive"
      });
      setFeedback(null);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Only render the component if user is logged in
  if (!user) {
    return null;
  }
  
  return (
    <div className="my-8 text-center">
      <h3 className="text-lg font-medium mb-4" dir="ltr">Was this {itemType === 'blog' ? 'article' : 'lesson'} helpful?</h3>
      <div className="flex justify-center gap-4">
        <button
          onClick={() => handleFeedback('dislike')}
          disabled={isSubmitting || feedback !== null}
          className={cn(
            "flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-200",
            feedback === 'dislike' 
              ? "bg-red-500 text-white" 
              : "bg-gray-50 hover:bg-red-50 border border-gray-200 hover:border-red-300 text-gray-700 hover:text-red-700",
            feedback !== null && feedback !== 'dislike' && "opacity-50 cursor-default"
          )}
        >
          <ThumbsDown className={cn("h-5 w-5", feedback === 'dislike' ? "text-white" : "text-red-500")} />
          <span className="font-medium">Not really</span>
        </button>
        <button
          onClick={() => handleFeedback('like')}
          disabled={isSubmitting || feedback !== null}
          className={cn(
            "flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-200",
            feedback === 'like' 
              ? "bg-[#34BC56] text-white" 
              : "bg-gray-50 hover:bg-green-50 border border-gray-200 hover:border-green-300 text-gray-700 hover:text-[#34BC56]",
            feedback !== null && feedback !== 'like' && "opacity-50 cursor-default"
          )}
        >
          <ThumbsUp className={cn("h-5 w-5", feedback === 'like' ? "text-white" : "text-[#34BC56]")} />
          <span className="font-medium">Yes, helpful!</span>
        </button>
      </div>
      {feedback && (
        <p className="text-[#34BC56] mt-4 text-sm">
          Thank you for your feedback!
        </p>
      )}
    </div>
  );
};

export default FeedbackQuestion;
